/**
 * AdminTopNav — horizontal navigation bar for admin routes.
 * Rendered ABOVE the standard TravelBio header, visible only when:
 *   • The logged-in user's role is EXACTLY "admin" (reviewer role
 *     does NOT see this — reviewers get the read-only admin views
 *     via the regular header + drawer, unchanged).
 *   • The current path starts with `/admin`.
 *
 * The bar mirrors the discrete admin routes as tabs and preserves an
 * "exit admin" affordance to jump back to /home. Styling is a stripe
 * of muted navy with a top gold border so it reads as an elevated
 * context (not a display ad).
 */
import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart2, MapPin, Layers, Search, Coins, Link as LinkIcon, LogOut,
} from "lucide-react";

const ADMIN_TABS = [
  { to: "/admin",                   label: "Dashboard",     Icon: BarChart2, exact: true,  testId: "admin-topnav-dashboard" },
  { to: "/admin/destinations",      label: "Destinations",  Icon: MapPin,    exact: false, testId: "admin-topnav-destinations" },
  { to: "/admin/cards",             label: "Cards",         Icon: Layers,    exact: false, testId: "admin-topnav-cards" },
  { to: "/admin/google-places",     label: "Google Places", Icon: Search,    exact: false, testId: "admin-topnav-gp" },
  // Phase D — Rewards admin console (atlas_admin only via router guard).
  { to: "/admin/rewards",           label: "Rewards",       Icon: Coins,     exact: false, testId: "admin-topnav-rewards" },
  // 2026-08-14 — Booking Link Overrides admin.
  { to: "/admin/booking-overrides", label: "Booking Links", Icon: LinkIcon,  exact: false, testId: "admin-topnav-booking-overrides" },
];

export default function AdminTopNav() {
  const loc = useLocation();
  const nav = useNavigate();
  return (
    <div
      data-testid="admin-topnav"
      className="shrink-0 w-full flex items-center justify-between gap-3 px-3 md:px-6 py-2 border-b-2 border-tb-gold/70 bg-tb-navy-deep text-tb-cream overflow-x-auto"
      style={{ boxShadow: "inset 0 3px 0 0 rgba(230,184,74,0.9)" }}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span
          data-testid="admin-topnav-label"
          className="hidden md:inline-block font-hand text-tb-gold-bright text-[10px] uppercase tracking-[0.22em] mr-2 whitespace-nowrap"
        >
          Owner console
        </span>
        {ADMIN_TABS.map(({ to, label, Icon, exact, testId }) => {
          const active = exact
            ? loc.pathname === to
            : (loc.pathname === to || loc.pathname.startsWith(to + "/"));
          return (
            <NavLink
              key={to}
              to={to}
              end={exact}
              data-testid={testId}
              className={
                "inline-flex items-center gap-1.5 h-9 px-3 rounded-full border tb-tap whitespace-nowrap transition-colors " +
                (active
                  ? "bg-tb-gold text-tb-navy-deep border-tb-gold-bright font-semibold"
                  : "bg-tb-navy/70 text-tb-cream border-tb-gold/40 hover:border-tb-gold hover:bg-tb-navy")
              }
              style={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.85rem" }}
            >
              <Icon className="w-4 h-4" strokeWidth={active ? 2.2 : 1.75} />
              {label}
            </NavLink>
          );
        })}
      </div>
      <button
        type="button"
        data-testid="admin-topnav-exit"
        onClick={() => nav("/home")}
        className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-gold/50 bg-tb-navy/70 text-tb-cream hover:bg-tb-coral/20 hover:border-tb-coral tb-tap"
        aria-label="Exit admin console"
        title="Exit admin console"
        style={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.85rem" }}
      >
        <LogOut className="w-4 h-4" />
        <span className="hidden sm:inline">Exit</span>
      </button>
    </div>
  );
}
